import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { useIsMobile } from '../hooks/useIsMobile'

const BLUE = '#185FA5'
const GREEN = '#2e7d32'

const STATUS_OPTIONS = ['not_contacted', 'contacted', 'interested', 'signed_up', 'not_interested']
const STATUS_LABELS = {
  not_contacted: { label: 'Not Contacted', color: '#757575', bg: '#f5f5f5' },
  contacted: { label: 'Contacted', color: '#1565c0', bg: '#e3f2fd' },
  interested: { label: 'Interested', color: '#e65100', bg: '#fff3e0' },
  signed_up: { label: 'Signed Up', color: '#2e7d32', bg: '#e8f5e9' },
  not_interested: { label: 'Not Interested', color: '#c62828', bg: '#ffebee' },
}

const COUNTIES = [
  'Antrim','Armagh','Carlow','Cavan','Clare','Cork','Derry','Donegal','Down',
  'Dublin','Fermanagh','Galway','Kerry','Kildare','Kilkenny','Laois','Leitrim',
  'Limerick','Longford','Louth','Mayo','Meath','Monaghan','Offaly','Roscommon',
  'Sligo','Tipperary','Tyrone','Waterford','Westmeath','Wexford','Wicklow'
]

export default function SiteAdminEmailTab() {
  const [subTab, setSubTab] = useState('compose')
  const isMobile = useIsMobile()

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid #e0e0e0' }}>
        {[
          { key: 'compose', label: '✉️ Compose' },
          { key: 'contacts', label: '📋 Club Contacts' },
          { key: 'history', label: '📨 Sent History' },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            padding: '8px 16px', background: 'none', border: 'none',
            borderBottom: subTab === t.key ? '2px solid ' + BLUE : '2px solid transparent',
            color: subTab === t.key ? BLUE : '#888',
            fontWeight: subTab === t.key ? 700 : 400,
            fontSize: 14, cursor: 'pointer', marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'compose' && <ComposeTab isMobile={isMobile} />}
      {subTab === 'contacts' && <ContactsTab isMobile={isMobile} />}
      {subTab === 'history' && <HistoryTab />}
    </div>
  )
}

// ─── COMPOSE TAB ─────────────────────────────────────────────────────────────

function ComposeTab({ isMobile }) {
  const [subject, setSubject] = useState('')
  const [blocks, setBlocks] = useState([{ type: 'paragraph', content: '' }])
  const [templates, setTemplates] = useState([])
  const [showTemplateSave, setShowTemplateSave] = useState(false)
  const [templateSaveName, setTemplateSaveName] = useState('')
  const [showTemplateLoad, setShowTemplateLoad] = useState(false)
  useEffect(() => { loadTemplates() }, [])
  async function loadTemplates() {
    const { data } = await supabase.from('email_templates').select('*').order('created_at', { ascending: false })
    setTemplates(data || [])
  }
  async function saveTemplate() {
    if (!templateSaveName.trim()) return
    const { error } = await supabase.from('email_templates').insert({ name: templateSaveName.trim(), subject, blocks })
    if (error) { window.alert('Failed to save template: ' + error.message); return }
    setTemplateSaveName('')
    setShowTemplateSave(false)
    loadTemplates()
  }
  function loadTemplate(t) {
    setSubject(t.subject)
    setBlocks(t.blocks)
    setShowTemplateLoad(false)
  }
  async function deleteTemplate(id) {
    if (!window.confirm('Delete this template?')) return
    await supabase.from('email_templates').delete().eq('id', id)
    loadTemplates()
  }
  const [recipientMode, setRecipientMode] = useState('bulk')
  const [recipientType, setRecipientType] = useState('all_admins')
  const [countyFilter, setCountyFilter] = useState('')
  const [preview, setPreview] = useState(false)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)
  const [recipientCount, setRecipientCount] = useState(0)
  const [recipientList, setRecipientList] = useState([])
  const [showConfirm, setShowConfirm] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [contactSearch, setContactSearch] = useState('')
  const [contactResults, setContactResults] = useState([])
  const [selectedContacts, setSelectedContacts] = useState([])

  useEffect(() => {
    if (recipientMode === 'bulk') loadRecipientCount()
  }, [recipientType, countyFilter, recipientMode])

  useEffect(() => {
    if (recipientMode === 'users' && userSearch.length > 1) searchUsers()
    else setUserResults([])
  }, [userSearch, recipientMode])

  useEffect(() => {
    if (recipientMode === 'contacts' && contactSearch.length > 1) searchContacts()
    else setContactResults([])
  }, [contactSearch, recipientMode])

  async function searchUsers() {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email, role, clubs(name)')
      .or(`full_name.ilike.%${userSearch}%,email.ilike.%${userSearch}%`)
      .not('email', 'is', null)
      .limit(10)
    setUserResults(data || [])
  }

  async function searchContacts() {
    const { data } = await supabase
      .from('club_contacts')
      .select('*')
      .or(`club_name.ilike.%${contactSearch}%,contact_name.ilike.%${contactSearch}%,contact_email.ilike.%${contactSearch}%`)
      .limit(10)
    setContactResults(data || [])
  }

  async function loadRecipientCount() {
    let query = supabase.from('profiles').select('*', { count: 'exact', head: true })
      .not('email', 'is', null)
    if (recipientType === 'all_admins') query = query.in('role', ['club_admin', 'squad_admin'])
    if (recipientType === 'club_admins') query = query.eq('role', 'club_admin')
    if (recipientType === 'all_users') query = query.not('club_id', 'is', null)
    if (countyFilter) {
      const { data: clubs } = await supabase.from('clubs').select('id').eq('county', countyFilter)
      const clubIds = (clubs || []).map(c => c.id)
      if (clubIds.length) query = query.in('club_id', clubIds)
      else { setRecipientCount(0); return }
    }
    const { count } = await query
    setRecipientCount(count || 0)
  }

  function toggleUser(user) {
    setSelectedUsers(prev =>
      prev.find(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]
    )
  }

  function toggleContact(contact) {
    setSelectedContacts(prev =>
      prev.find(c => c.id === contact.id) ? prev.filter(c => c.id !== contact.id) : [...prev, contact]
    )
  }

  function getEffectiveCount() {
    if (recipientMode === 'users') return selectedUsers.length
    if (recipientMode === 'contacts') return selectedContacts.length
    return recipientCount
  }

  function getEffectiveEmails() {
    if (recipientMode === 'users') return selectedUsers.map(u => u.email)
    if (recipientMode === 'contacts') return selectedContacts.map(c => c.contact_email).filter(Boolean)
    return []
  }

  function addBlock(type) {
    setBlocks(prev => [...prev, { type, content: '', url: type === 'button' ? 'https://gaamatchtracker.ie' : undefined }])
  }

  function updateBlock(index, field, value) {
    setBlocks(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b))
  }

  function removeBlock(index) {
    setBlocks(prev => prev.filter((_, i) => i !== index))
  }

  function moveBlock(index, direction) {
    const newBlocks = [...blocks]
    const swap = index + direction
    if (swap < 0 || swap >= newBlocks.length) return
    ;[newBlocks[index], newBlocks[swap]] = [newBlocks[swap], newBlocks[index]]
    setBlocks(newBlocks)
  }

  function buildHtml() {
    return blocks.map(b => {
      if (b.type === 'heading') return `<h2 style="color: #185FA5; font-size: 18px; font-weight: 700; margin: 20px 0 8px;">${b.content}</h2>`
      if (b.type === 'paragraph') return `<p style="color: #555; line-height: 1.7; margin: 0 0 14px;">${b.content.replace(/\n/g, '<br/>')}</p>`
      if (b.type === 'button') return `<div style="text-align: center; margin: 24px 0;"><a href="${b.url}" style="background: #185FA5; color: white; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-size: 15px; font-weight: 600; display: inline-block;">${b.content}</a></div>`
      if (b.type === 'list') {
        const items = b.content.split('\n').filter(l => l.trim()).map(l => `<li style="margin-bottom: 6px;">${l}</li>`).join('')
        return `<ul style="color: #555; line-height: 1.9; padding-left: 20px; margin: 0 0 14px;">${items}</ul>`
      }
      return ''
    }).join('')
  }

  async function handleSend() {
    if (!subject.trim()) { setError('Please enter a subject line.'); return }
    if (blocks.every(b => !b.content.trim())) { setError('Please add some content.'); return }
    if (getEffectiveCount() === 0) { setError('No recipients selected.'); return }
    setShowConfirm(true)
  }

  async function confirmSend() {
    setSending(true)
    setError(null)
    setShowConfirm(false)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const bodyData = {
        type: 'manual_bulk',
        subject,
        html_blocks: buildHtml(),
        recipient_type: recipientMode === 'bulk' ? recipientType : 'specific',
        county_filter: recipientMode === 'bulk' ? (countyFilter || null) : null,
        specific_emails: recipientMode !== 'bulk' ? getEffectiveEmails() : null,
      }
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-automated-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.access_token },
        body: JSON.stringify(bodyData),
      })
      if (!res.ok) {
        let detail = ''
        try { detail = (await res.json())?.error || '' } catch (_) {}
        throw new Error(detail || ('Failed to send (status ' + res.status + ')'))
      }
      setSent(true)
    } catch (e) {
      setError('Failed to send: ' + e.message)
    }
    setSending(false)
  }

  const effectiveCount = getEffectiveCount()
  const effectiveEmails = getEffectiveEmails()

  if (sent) return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: GREEN, marginBottom: 8 }}>Email sent!</div>
      <div style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>Sent to {effectiveCount} recipients.</div>
      <button onClick={() => { setSent(false); setSubject(''); setBlocks([{ type: 'paragraph', content: '' }]); setSelectedUsers([]); setSelectedContacts([]) }}
        style={{ padding: '10px 24px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
        Compose another
      </button>
    </div>
  )

  return (
    <div>
      {/* Confirm modal */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 440, width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', marginBottom: 8 }}>Confirm Send</div>
            <div style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
              You are about to send <strong>"{subject}"</strong> to <strong>{effectiveCount} recipient{effectiveCount !== 1 ? 's' : ''}</strong>. This cannot be undone.
            </div>
            {effectiveEmails.length > 0 && (
              <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 12, marginBottom: 16, maxHeight: 150, overflowY: 'auto' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 6 }}>Recipients:</div>
                {effectiveEmails.slice(0, 10).map((e, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#555', padding: '2px 0' }}>{e}</div>
                ))}
                {effectiveEmails.length > 10 && <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>+{effectiveEmails.length - 10} more</div>}
              </div>
            )}
            {effectiveEmails.length === 0 && recipientMode === 'bulk' && (
              <div style={{ background: '#f5f7fa', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#555' }}>
                Sending to {recipientCount} {recipientType === 'all_admins' ? 'club & squad admins' : recipientType === 'club_admins' ? 'club admins' : 'registered users'}{countyFilter ? ' in ' + countyFilter : ''}.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowConfirm(false)}
                style={{ flex: 1, padding: '11px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={confirmSend}
                style={{ flex: 2, padding: '11px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        {/* Editor */}
        <div style={{ flex: 2, minWidth: 0 }}>
          {error && <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 14 }}>{error}</div>}

          {/* Recipients */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Recipients</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[
                { key: 'bulk', label: '👥 Bulk' },
                { key: 'users', label: '🔍 Find Users' },
                { key: 'contacts', label: '📋 Contacts' },
              ].map(m => (
                <button key={m.key} onClick={() => setRecipientMode(m.key)} style={{
                  padding: '6px 12px', border: '1px solid ' + (recipientMode === m.key ? BLUE : '#ddd'),
                  background: recipientMode === m.key ? '#e8f0fb' : '#fff',
                  color: recipientMode === m.key ? BLUE : '#555',
                  borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: recipientMode === m.key ? 600 : 400,
                }}>
                  {m.label}
                </button>
              ))}
            </div>

            {recipientMode === 'bulk' && (
              <>
                <select value={recipientType} onChange={e => setRecipientType(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginBottom: 10, background: '#fff' }}>
                  <option value="all_admins">All club & squad admins</option>
                  <option value="club_admins">Club admins only</option>
                  <option value="all_users">All registered users</option>
                </select>
                <select value={countyFilter} onChange={e => setCountyFilter(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginBottom: 10, background: '#fff' }}>
                  <option value="">All counties</option>
                  {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{ fontSize: 13, color: '#888' }}>
                  📬 <strong style={{ color: BLUE }}>{recipientCount}</strong> recipient{recipientCount !== 1 ? 's' : ''} match this filter
                </div>
              </>
            )}

            {recipientMode === 'users' && (
              <>
                <input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
                />
                {userResults.map(u => (
                  <div key={u.id} onClick={() => toggleUser(u)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: selectedUsers.find(s => s.id === u.id) ? '#e8f0fb' : '#f5f5f5', marginBottom: 4 }}>
                    <input type="checkbox" readOnly checked={!!selectedUsers.find(s => s.id === u.id)} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{u.full_name || u.email}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{u.email} · {u.clubs?.name || 'No club'} · {u.role}</div>
                    </div>
                  </div>
                ))}
                {selectedUsers.length > 0 && (
                  <div style={{ fontSize: 13, color: BLUE, fontWeight: 600, marginTop: 8 }}>
                    ✓ {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </>
            )}

            {recipientMode === 'contacts' && (
              <>
                <input
                  placeholder="Search club contacts..."
                  value={contactSearch}
                  onChange={e => setContactSearch(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
                />
                {contactResults.map(c => (
                  <div key={c.id} onClick={() => toggleContact(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: selectedContacts.find(s => s.id === c.id) ? '#e8f0fb' : '#f5f5f5', marginBottom: 4 }}>
                    <input type="checkbox" readOnly checked={!!selectedContacts.find(s => s.id === c.id)} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.club_name}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{c.contact_email || 'No email'} · {c.county || ''}</div>
                    </div>
                  </div>
                ))}
                {selectedContacts.length > 0 && (
                  <div style={{ fontSize: 13, color: BLUE, fontWeight: 600, marginTop: 8 }}>
                    ✓ {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </>
            )}
          </div>
          {/* Templates */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => setShowTemplateLoad(true)}
              style={{ padding: '8px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#555' }}>
              📂 Load Template
            </button>
            <button onClick={() => setShowTemplateSave(true)}
              style={{ padding: '8px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#555' }}>
              💾 Save as Template
            </button>
          </div>
          {showTemplateSave && (
            <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={templateSaveName} onChange={e => setTemplateSaveName(e.target.value)} placeholder="Template name..."
                style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }} />
              <button onClick={saveTemplate} style={{ padding: '9px 16px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Save</button>
              <button onClick={() => { setShowTemplateSave(false); setTemplateSaveName('') }} style={{ padding: '9px 14px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            </div>
          )}
          {showTemplateLoad && (
            <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              {templates.length === 0 ? (
                <div style={{ color: '#999', fontSize: 14 }}>No saved templates yet.</div>
              ) : templates.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span onClick={() => loadTemplate(t)} style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600, color: BLUE }}>{t.name}</span>
                  <button onClick={() => deleteTemplate(t.id)} style={{ padding: '3px 10px', background: '#fdecea', color: '#c62828', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </div>
              ))}
              <button onClick={() => setShowTemplateLoad(false)} style={{ marginTop: 10, padding: '7px 14px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          )}
          {/* Subject */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Subject</div>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line..."
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {/* Content blocks */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Content</div>
            {blocks.map((block, i) => (
              <div key={i} style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 12, marginBottom: 10, background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase' }}>{block.type}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <button onClick={() => moveBlock(i, -1)} style={{ padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>↑</button>
                    <button onClick={() => moveBlock(i, 1)} style={{ padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>↓</button>
                    <button onClick={() => removeBlock(i)} style={{ padding: '2px 6px', border: '1px solid #f5c6cb', borderRadius: 4, background: '#fff3f3', cursor: 'pointer', fontSize: 11, color: '#c0392b' }}>✕</button>
                  </div>
                </div>
                {block.type === 'button' && (
                  <input value={block.url || ''} onChange={e => updateBlock(i, 'url', e.target.value)}
                    placeholder="Button URL" style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, marginBottom: 6, outline: 'none', boxSizing: 'border-box' }} />
                )}
                <textarea value={block.content} onChange={e => updateBlock(i, 'content', e.target.value)}
                  placeholder={block.type === 'heading' ? 'Heading text...' : block.type === 'paragraph' ? 'Paragraph text...' : block.type === 'button' ? 'Button label...' : 'List items (one per line)...'}
                  rows={block.type === 'list' ? 4 : block.type === 'paragraph' ? 3 : 1}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {['heading', 'paragraph', 'button', 'list'].map(type => (
                <button key={type} onClick={() => addBlock(type)}
                  style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555' }}>
                  + {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setPreview(!preview)}
              style={{ flex: 1, padding: '11px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              {preview ? '✏️ Edit' : '👁 Preview'}
            </button>
            <button onClick={handleSend} disabled={sending || effectiveCount === 0}
              style={{ flex: 2, padding: '11px', background: effectiveCount === 0 ? '#ccc' : BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: effectiveCount === 0 ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, opacity: sending ? 0.6 : 1 }}>
              {sending ? 'Sending...' : `📨 Send to ${effectiveCount} recipient${effectiveCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>

        {/* Preview */}
        {(preview || !isMobile) && (
          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 260 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Preview</div>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden', fontSize: 13 }}>
              <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', padding: '20px 16px', textAlign: 'center' }}>
                <img src="https://gaamatchtracker.ie/apple-touch-icon.png" alt="GAA Match Tracker" width="48" height="48" style={{ borderRadius: 10, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>GAA Match Tracker</div>
                <div style={{ color: '#B5D4F4', fontSize: 11, marginTop: 2 }}>Real-time coaching intelligence</div>
              </div>
              <div style={{ background: '#fff', padding: '16px' }}>
                {subject && <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>Subject: {subject}</div>}
                <div dangerouslySetInnerHTML={{ __html: buildHtml() }} />
                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 12, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
                  GAA Match Tracker · gaamatchtracker.ie
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── CONTACTS TAB ─────────────────────────────────────────────────────────────

function ContactsTab({ isMobile }) {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [countyFilter, setCountyFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editContact, setEditContact] = useState(null)

  useEffect(() => { loadContacts() }, [])

  async function loadContacts() {
    setLoading(true)
    const { data } = await supabase.from('club_contacts').select('*').order('created_at', { ascending: false })
    const list = data || []
    const profileIds = [...new Set(list.map(c => c.profile_id).filter(Boolean))]
    let profilesById = {}
    let squadsByProfile = {}
    if (profileIds.length > 0) {
      const { data: profiles } = await supabase.from('profiles').select('id, role, approved').in('id', profileIds)
      profilesById = Object.fromEntries((profiles || []).map(p => [p.id, p]))
      const { data: memberships } = await supabase.from('squad_members').select('profile_id, squads(name)').in('profile_id', profileIds)
      ;(memberships || []).forEach(m => {
        const name = m.squads?.name
        if (!name) return
        if (!squadsByProfile[m.profile_id]) squadsByProfile[m.profile_id] = []
        squadsByProfile[m.profile_id].push(name)
      })
    }
    const enriched = list.map(c => ({
      ...c,
      _role: c.profile_id ? profilesById[c.profile_id]?.role : null,
      _approved: c.profile_id ? profilesById[c.profile_id]?.approved : null,
      _squads: c.profile_id ? (squadsByProfile[c.profile_id] || []) : []
    }))
    setContacts(enriched)
    setLoading(false)
  }

  const filtered = contacts.filter(c => {
    const matchSearch = !search || c.club_name?.toLowerCase().includes(search.toLowerCase()) || c.contact_name?.toLowerCase().includes(search.toLowerCase()) || c.contact_email?.toLowerCase().includes(search.toLowerCase())
    const matchCounty = !countyFilter || c.county === countyFilter
    const matchStatus = !statusFilter || c.status === statusFilter
    const matchRole = !roleFilter || c._role === roleFilter
    return matchSearch && matchCounty && matchStatus && matchRole
  })

  const counties = [...new Set(contacts.map(c => c.county).filter(Boolean))].sort()

  if (showForm) return (
    <ContactForm
      contact={editContact}
      onSave={() => { loadContacts(); setShowForm(false); setEditContact(null) }}
      onCancel={() => { setShowForm(false); setEditContact(null) }}
    />
  )

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search clubs or contacts..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 2, minWidth: 160, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }} />
        <select value={countyFilter} onChange={e => setCountyFilter(e.target.value)}
          style={{ flex: 1, minWidth: 120, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff' }}>
          <option value="">All counties</option>
          {counties.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ flex: 1, minWidth: 140, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff' }}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s].label}</option>)}
        </select>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          style={{ flex: 1, minWidth: 130, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff' }}>
          <option value="">All roles</option>
          <option value="club_admin">Club Admin</option>
          <option value="squad_admin">Squad Admin</option>
          <option value="coach">Coach</option>
        </select>
        <button onClick={() => { setEditContact(null); setShowForm(true) }}
          style={{ padding: '9px 16px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>
          + Add Contact
        </button>
      </div>

      <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div> : (
        isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(c => (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.club_name}</div>
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: STATUS_LABELS[c.status]?.bg, color: STATUS_LABELS[c.status]?.color }}>
                    {STATUS_LABELS[c.status]?.label}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{c.county || '—'}</div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{c.contact_name || '—'} · {c.contact_email || '—'}</div>
                {c.profile_id && (
                  <div style={{ fontSize: 12, marginBottom: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ padding: '2px 7px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}>Registered</span>
                    {c._role && <span style={{ color: '#666' }}>{c._role}</span>}
                    {c._approved === false && <span style={{ color: '#c62828' }}>· Not approved</span>}
                    {c._squads.length > 0 && <span style={{ color: '#999' }}>· {c._squads.join(', ')}</span>}
                  </div>
                )}
                {c.notes && <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{c.notes}</div>}
                <button onClick={() => { setEditContact(c); setShowForm(true) }}
                  style={{ padding: '5px 12px', background: '#f0f0f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Edit</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f7fa' }}>
                  {['Club', 'County', 'Contact', 'Email', 'Status', 'Role / Squad', 'Notes', ''].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 13 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No contacts yet — add your first club contact</td></tr>
                ) : filtered.map((c, i) => (
                  <tr key={c.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.club_name}</td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>{c.county || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>{c.contact_name || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>{c.contact_email || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: STATUS_LABELS[c.status]?.bg, color: STATUS_LABELS[c.status]?.color }}>
                        {STATUS_LABELS[c.status]?.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      {c.profile_id ? (
                        <div>
                          <span style={{ padding: '2px 7px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, marginRight: 4 }}>Registered</span>
                          <div style={{ color: '#666', marginTop: 3 }}>
                            {c._role}{c._approved === false ? ' · Not approved' : ''}
                          </div>
                          {c._squads.length > 0 && <div style={{ color: '#999', marginTop: 2 }}>{c._squads.join(', ')}</div>}
                        </div>
                      ) : <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#999', fontSize: 12, maxWidth: 200 }}>{c.notes || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <button onClick={() => { setEditContact(c); setShowForm(true) }}
                        style={{ padding: '4px 10px', background: '#f0f0f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}

function ContactForm({ contact, onSave, onCancel }) {
  const [form, setForm] = useState({
    club_name: contact?.club_name || '',
    county: contact?.county || '',
    contact_name: contact?.contact_name || '',
    contact_email: contact?.contact_email || '',
    phone: contact?.phone || '',
    status: contact?.status || 'not_contacted',
    notes: contact?.notes || '',
    signed_up: contact?.signed_up || false,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function update(field, value) { setForm(prev => ({ ...prev, [field]: value })) }

  async function save() {
    if (!form.club_name.trim()) { setError('Club name is required.'); return }
    setSaving(true)
    setError(null)
    if (contact?.id) {
      await supabase.from('club_contacts').update(form).eq('id', contact.id)
    } else if (form.contact_email?.trim()) {
      const email = form.contact_email.trim().toLowerCase()
      const { data: existing } = await supabase
        .from('club_contacts').select('id').eq('contact_email', email).maybeSingle()
      if (existing) {
        setError('A contact with this email already exists — editing that contact instead.')
        await supabase.from('club_contacts').update({ ...form, contact_email: email }).eq('id', existing.id)
      } else {
        await supabase.from('club_contacts').insert({ ...form, contact_email: email })
      }
    } else {
      await supabase.from('club_contacts').insert(form)
    }
    setSaving(false)
    onSave()
  }

  async function deleteContact() {
    if (!window.confirm('Delete this contact?')) return
    await supabase.from('club_contacts').delete().eq('id', contact.id)
    onSave()
  }

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }

  return (
    <div>
      <button onClick={onCancel} style={{ marginBottom: 16, padding: '7px 14px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
        ← Back
      </button>
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: BLUE, marginBottom: 16 }}>
          {contact ? 'Edit Contact' : 'Add Club Contact'}
        </div>
        {error && <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#c0392b', fontSize: 14 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Club Name *</div>
            <input value={form.club_name} onChange={e => update('club_name', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>County</div>
            <select value={form.county} onChange={e => update('county', e.target.value)} style={{ ...inputStyle, background: '#fff' }}>
              <option value="">Select county</option>
              {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Contact Name</div>
            <input value={form.contact_name} onChange={e => update('contact_name', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Contact Email</div>
            <input type="email" value={form.contact_email} onChange={e => update('contact_email', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Phone</div>
            <input value={form.phone} onChange={e => update('phone', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Status</div>
            <select value={form.status} onChange={e => update('status', e.target.value)} style={{ ...inputStyle, background: '#fff' }}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s].label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#888', marginBottom: 4 }}>Notes</div>
          <textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <input type="checkbox" id="signed_up" checked={form.signed_up} onChange={e => update('signed_up', e.target.checked)} />
          <label htmlFor="signed_up" style={{ fontSize: 14, color: '#555' }}>Already signed up on GAA Match Tracker</label>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={save} disabled={saving}
            style={{ flex: 2, padding: '11px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact'}
          </button>
          {contact && (
            <button onClick={deleteContact}
              style={{ flex: 1, padding: '11px', background: '#fff3f3', color: '#c0392b', border: '1px solid #f5c6cb', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── HISTORY TAB ─────────────────────────────────────────────────────────────

function HistoryTab() {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: '#aaa', background: '#fafafa', borderRadius: 12, border: '1px dashed #ddd' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#888', marginBottom: 8 }}>Coming soon</div>
      <div style={{ fontSize: 13 }}>Email send history will appear here</div>
    </div>
  )
}